import React, { useEffect, useState } from 'react'
import { authAPI } from '../api/client'

/**
 * Register page component.
 *
 * Provides registration form for new users to create an account.
 * Displays:
 * - Username input field
 * - Email input field
 * - Password input field
 * - Confirm password input field
 * - Submit button
 * - Error messages (if registration fails)
 */

export interface RegisterProps {
  /** Page heading; the admin bundle passes "hPanel Admin". */
  heading?: string
}

export const Register: React.FC<RegisterProps> = ({ heading = 'hPanel' }) => {
  const [registered, setRegistered] = useState(false)
  // null = unknown (still loading, or the probe failed). Unknown shows the
  // form: POST /register is the authority and will refuse with its own detail.
  const [signupOpen, setSignupOpen] = useState<boolean | null>(null)

  useEffect(() => {
    authAPI
      .registrationStatus()
      .then((r) => setSignupOpen(typeof r?.enabled === 'boolean' ? r.enabled : null))
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('[Register] registration status probe failed', e)
      })
  }, [])
  const [username, setUsername] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // Validate passwords match
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setIsLoading(true)

    try {
      await authAPI.register({ username, email, password })
      setRegistered(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div data-testid="register-page" className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* Header */}
        <div>
          <h1 className="text-center text-3xl font-bold text-gray-900 dark:text-gray-100">
            {heading}
          </h1>
          <h2 className="mt-6 text-center text-2xl font-semibold text-gray-700 dark:text-gray-300">
            Create your account
          </h2>
        </div>

        {signupOpen === false ? (
          <div className="text-center space-y-4">
            <p data-testid="register-disabled" className="text-gray-700 dark:text-gray-300">
              Self sign-up is turned off on this server. Ask your hosting provider to create an account for you.
            </p>
            <a href="/login" className="text-blue-600 underline">Back to sign in</a>
          </div>
        ) : registered ? (
          <div role="status" className="text-center space-y-4">
            <p>Account created. You can now sign in.</p>
            <a href="/login" className="text-blue-600 underline">Sign in</a>
          </div>
        ) : <>
        {/* Register Form */}
        <form data-testid="register-form" className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm space-y-4">
            {/* Username Field */}
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Username
              </label>
              <input
                id="username"
                name="username"
                data-testid="register-username-input"
                type="text"
                autoComplete="username"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Enter your username"
              />
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email
              </label>
              <input
                id="email"
                name="email"
                data-testid="register-email-input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Enter your email"
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                data-testid="register-password-input"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Enter your password"
              />
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Confirm Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                data-testid="register-confirm-password-input"
                type="password"
                autoComplete="new-password"
                required
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm"
                placeholder="Confirm your password"
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div data-testid="register-error" role="alert" className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
              <p className="text-sm text-red-800 dark:text-red-400">{error}</p>
            </div>
          )}

          {/* Submit Button */}
          <div>
            <button
              type="submit"
              data-testid="register-submit-button"
              disabled={isLoading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Creating account...' : 'Create account'}
            </button>
          </div>
        </form>
        </>}
      </div>
    </div>
  )
}
